import { useState } from 'react';
import { useAuthStore } from '../store/auth';
import { useUIStore } from '../store/ui';
import { apiService } from '../services/api';

interface SubscriptionPlan {
  id: string;
  name: string;
  days: number;
  stars: number;
  popular?: boolean;
  savings?: string;
}

export default function Subscription() {
  const { user } = useAuthStore();
  const { showToast, setLoading } = useUIStore();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const plans: SubscriptionPlan[] = [
    { id: '7days', name: '7 дней', days: 7, stars: 50 },
    { id: '14days', name: '14 дней', days: 14, stars: 90, savings: 'Экономия 10 ⭐' },
    { id: '30days', name: '30 дней', days: 30, stars: 150, popular: true, savings: 'Экономия 64 ⭐' },
    { id: '365days', name: '365 дней', days: 365, stars: 1000, savings: 'Экономия 825 ⭐' }
  ];

  const features = [
    { icon: '♾️', title: 'Безлимитные объявления', desc: 'Размещайте сколько угодно' },
    { icon: '🚀', title: 'Продвижение', desc: 'Ваши объявления в топе' },
    { icon: '📊', title: 'Статистика', desc: 'Просмотры и аналитика' },
    { icon: '✅', title: 'Верификация', desc: 'Значок проверенного продавца' },
    { icon: '💬', title: 'Приоритет в чатах', desc: 'Быстрые ответы поддержки' }
  ];

  const handleSubscribe = async () => {
    if (!selectedPlan || !user) return;

    const plan = plans.find((p) => p.id === selectedPlan);
    if (!plan) return;

    setLoading(true);
    try {
      // Create Telegram Stars invoice
      const tg = window.Telegram?.WebApp;
      if (!tg) {
        throw new Error('Telegram WebApp not available');
      }

      // Open invoice through bot
      const response = await apiService.createSubscriptionInvoice({
        userId: user.id,
        planId: selectedPlan,
        days: plan.days,
        stars: plan.stars
      });

      const invoiceLink = response.data.data.invoiceLink;
      
      tg.openInvoice(invoiceLink, (status) => {
        if (status === 'paid') {
          window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
          showToast('Подписка активирована! 🎉', 'success');
          // Refresh user data
          window.location.reload();
        } else if (status === 'cancelled') {
          showToast('Оплата отменена', 'info');
        } else if (status === 'failed') {
          showToast('Ошибка оплаты', 'error');
        }
      });
    } catch (error) {
      console.error('Error creating invoice:', error);
      showToast('Ошибка при создании платежа', 'error');
    } finally {
      setLoading(false);
    }
  };

  const activeSub = user?.subscription;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <span className="text-5xl">💎</span>
        <h1 className="text-2xl font-bold mt-2">Premium подписка</h1>
        <p className="text-tg-theme-hint mt-1">Получите максимум от Биржи</p>
      </div>

      {/* Active Subscription */}
      {activeSub && new Date(activeSub.expiresAt) > new Date() && (
        <div className="card bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Ваша подписка</p>
              <p className="font-bold text-lg">Premium активен ✓</p>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-90">Действует до</p>
              <p className="font-bold">
                {new Date(activeSub.expiresAt).toLocaleDateString('ru-RU')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Features */}
      <div className="card">
        <h2 className="font-semibold mb-3">Что входит в Premium:</h2>
        <div className="space-y-3">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3">
              <span className="text-xl">{feature.icon}</span>
              <div>
                <p className="font-medium">{feature.title}</p>
                <p className="text-sm text-tg-theme-hint">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Plans */}
      <div className="space-y-3">
        <h2 className="font-semibold">Выберите план:</h2>
        {plans.map((plan) => (
          <button
            key={plan.id}
            onClick={() => setSelectedPlan(plan.id)}
            className={`w-full p-4 rounded-xl border-2 transition-all text-left relative ${
              selectedPlan === plan.id
                ? 'border-tg-theme-button bg-tg-theme-button/10'
                : 'border-tg-theme-hint/20 bg-tg-theme-secondary-bg'
            }`}
          >
            {plan.popular && (
              <span className="absolute -top-2 right-4 bg-tg-theme-button text-white text-xs px-2 py-0.5 rounded-full">
                Популярный
              </span>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-lg">{plan.name}</p>
                {plan.savings && (
                  <p className="text-sm text-green-500">{plan.savings}</p>
                )}
              </div>
              <div className="text-right">
                <p className="font-bold text-xl">
                  {plan.stars} <span className="text-yellow-500">⭐</span>
                </p>
                <p className="text-xs text-tg-theme-hint">
                  {Math.round(plan.stars / plan.days * 100) / 100} ⭐/день
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Subscribe Button */}
      <button
        onClick={handleSubscribe}
        disabled={!selectedPlan}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed text-lg py-4"
      >
        {selectedPlan
          ? `Оплатить ${plans.find((p) => p.id === selectedPlan)?.stars} ⭐`
          : 'Выберите план'}
      </button>

      {/* Info */}
      <div className="text-center text-xs text-tg-theme-hint space-y-1">
        <p>Оплата через Telegram Stars</p>
        <p>Подписка активируется мгновенно</p>
        <p>Автопродление отсутствует</p>
      </div>
    </div>
  );
}
